/**
 * The settings screens' option lists — READ from the server
 * (`meta.policy.options`, `sdlc/policy.public_view`), never kept here.
 *
 * What this file does own is the WORDING: the server sends ids
 * (`auto_low_risk`), a person reads a sentence. An id this file has no
 * sentence for is still offered, with a readable form of the id itself, so a
 * mode the server adds tomorrow appears in the select without a release of
 * this package. The fallback lists exist only for a server that predates
 * `options` and are used when it sends none.
 */

import type { SupportMeta, SupportPolicyOptions } from './contract'

const FALLBACK: Required<Pick<SupportPolicyOptions,
  'release_mode' | 'github_mode' | 'auto_upgrade' | 'note_groups'>> & {
  gates: Record<string, string[]>
} = {
  gates: {
    auto_analyse: ['by_type', 'all', 'none'],
    plan_approval: ['human', 'auto_low_risk'],
    qc: ['human', 'agent_low_risk'],
    pm_approval: ['human', 'auto_low_risk'],
  },
  release_mode: ['pm_bundle', 'per_approval'],
  github_mode: ['auto', 'direct', 'pull_request'],
  auto_upgrade: ['off', 'patch', 'minor'],
  note_groups: ['New', 'Improved', 'Fixed'],
}

const LABELS: Record<string, Record<string, string>> = {
  auto_analyse: {
    by_type: 'By issue type',
    all: 'Everything',
    none: 'Nothing — a person starts each analysis',
  },
  plan_approval: {
    human: 'A developer or project manager approves every plan',
    auto_low_risk: 'The AI may approve low-risk plans it is confident about',
  },
  qc: {
    human: 'A tester checks every fix on staging',
    agent_low_risk: 'An AI acceptance run is enough for low-risk fixes',
  },
  pm_approval: {
    human: 'A project manager approves every fix for release',
    auto_low_risk: 'Automatic once QC passed a low-risk fix',
  },
  release_mode: {
    pm_bundle: 'A project manager bundles approved fixes into a release',
    per_approval: 'Every approved fix is released on its own, automatically',
  },
  github_mode: {
    auto: 'Automatic — a pull request when the branch is protected',
    pull_request: 'Always through a pull request',
    direct: 'Push straight to the branch',
  },
  auto_upgrade: {
    off: 'Never — I upgrade by hand',
    patch: 'Fix releases only (x.y.Z)',
    minor: 'Fix and feature releases (x.Y.z)',
  },
}

/** A readable form of an id nobody wrote a sentence for. */
function humanize(id: string): string {
  const s = id.replace(/_/g, ' ')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export interface OptionItem { id: string; label: string }

function items(kind: string, ids: string[] | undefined, fallback: string[]): OptionItem[] {
  const list = ids && ids.length ? ids : fallback
  return list.map((id) => ({ id, label: LABELS[kind]?.[id] || humanize(id) }))
}

/** The options of one gate (`auto_analyse`, `plan_approval`, `qc`, `pm_approval`). */
export function gateOptions(meta: SupportMeta | null | undefined, gate: string): OptionItem[] {
  return items(gate, meta?.policy?.options?.gates?.[gate], FALLBACK.gates[gate] || [])
}

export function releaseModeOptions(meta: SupportMeta | null | undefined): OptionItem[] {
  return items('release_mode', meta?.policy?.options?.release_mode, FALLBACK.release_mode)
}

export function githubModeOptions(meta: SupportMeta | null | undefined): OptionItem[] {
  return items('github_mode', meta?.policy?.options?.github_mode, FALLBACK.github_mode)
}

/** Auto-upgrade modes; `ids` lets a host pass the list when it holds no meta. */
export function autoUpgradeOptions(ids?: string[] | null): OptionItem[] {
  return items('auto_upgrade', ids || undefined, FALLBACK.auto_upgrade)
}

/** The release-note groups, in the order the notes are rendered. */
export function noteGroups(meta: SupportMeta | null | undefined): string[] {
  const g = meta?.policy?.options?.note_groups
  return g && g.length ? g : FALLBACK.note_groups
}
